import { Api } from 'lavva.exalushome';
import { IDIService } from 'lavva.exalushome/build/js/IDIService';
import { DependencyContainer } from 'lavva.exalushome/build/js/DependencyContainer';
import { ILoggerService } from 'lavva.exalushome/build/js/Services/Logging/ILoggerService';
import { LoggerService } from 'lavva.exalushome/build/js/Services/Logging/LoggerService';
import { IControllerExtensionsService } from 'lavva.exalushome/build/js/Services/Controller/IControllerExtensionsService';
import { ControllerExtensionsService } from 'lavva.exalushome/build/js/Services/Controller/ControllerExtensionsService';
import { IManuallyPairedDevicesService } from 'lavva.exalushome/build/js/Services/Devices/IManuallyPairedDevicesService';
import { ManuallyPairedDevicesService } from 'lavva.exalushome/build/js/Services/Devices/ManuallyPairedDevicesService';
import { ManuallyPairedDeviceService } from './Devices/DevicePairing/ManuallyPairedDeviceService';
import { IDeviceConfigService } from 'lavva.exalushome/build/js/Services/Devices/IDeviceConfigService';
import { Portos433ConfigService } from './Devices/DeviceServices/Portos433Mhz/Portos433ConfigService';
import { IDevice } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IUpdatesProvider } from 'lavva.exalushome/build/js/Services/Updates/IUpdatesProvider';
import { UpdatesProvider } from 'lavva.exalushome/build/js/Services/Updates/UpdatesProvider';
import { PortosDevicesUpdatesProvider } from './Devices/Updates/PortosDevicesUpdatesProvider';
import { PortosRadioUpdatesProvider } from './Controller/Updates/PortosRadioUpdatesProvider';

export class PortosService implements IDIService {
	public static readonly ServiceName: string = "PortosService";
	public static readonly PortosExtensionGuid = "ca9c9dee-8bf6-4688-a04f-1ce35693405f";
	public static readonly PortosProtocolGuid = "cd25bdff-628c-4dbc-a16a-648c96a819d4";
	public static readonly PortosRadioModuleDeviceGuid = "b015ecf2-14c9-4b35-81f1-d008f3a75442";

	private _services: Record<string, IDeviceConfigService> = {
		Portos433ConfigService: new Portos433ConfigService()
	};
	private _servicesToDeviceMap: Map<string, IDeviceConfigService[]> = new Map([
		//Portos 433Mhz
		["080844eb-3ec1-4611-a327-757a634bd866", [this._services.Portos433ConfigService]], // for virtual device
		["179b750c-5c73-4a6c-865c-5cf85a27200b", [this._services.Portos433ConfigService]], // for remotes
	]);

	GetServiceName(): string {
		return PortosService.ServiceName;
	}

	static Init(): void {
		DependencyContainer.Instance.RegisterService(new PortosService());
		Api.Get<ILoggerService>(LoggerService.ServiceName).Debug(PortosService.ServiceName, "Initialized PortosService");

		PortosService.RegisterManuallyPairedDeviceServiceAsync();
		PortosService.RegisterUpdateProviderAsync();
	}

	protected static async RegisterManuallyPairedDeviceServiceAsync() {
		const extensionInfo = (await Api.Get<IControllerExtensionsService>(ControllerExtensionsService.ServiceName).GetExtensionsInfoAsync(PortosService.PortosExtensionGuid)).first();

		if(extensionInfo != null){
			Api.Get<ILoggerService>(LoggerService.ServiceName).Debug(PortosService.ServiceName, "Registering Portos manually paired device service.");
			Api.Get<IManuallyPairedDevicesService>(ManuallyPairedDevicesService.ServiceName).RegisterManuallyPairedDevicesProtocol(new ManuallyPairedDeviceService(extensionInfo));
		}
		else {
			Api.Get<ILoggerService>(LoggerService.ServiceName).Warning(PortosService.ServiceName, "Portos manually paired device service not registered - controller does not support Portos extension.");
		}
	}

	protected static async RegisterUpdateProviderAsync() {
		const updateProvider = await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName);

		const extensionInfo = (await Api.Get<IControllerExtensionsService>(ControllerExtensionsService.ServiceName).GetExtensionsInfoAsync(PortosService.PortosExtensionGuid)).first();
		const runtimeInfo = await updateProvider.GetSoftwareRuntimeInfoAsync();

		if(extensionInfo != null){
			Api.Get<ILoggerService>(LoggerService.ServiceName).Debug(PortosService.ServiceName, "Registering Portos update providers.");
			updateProvider?.RegisterUpdatesProvider(new PortosDevicesUpdatesProvider(extensionInfo, runtimeInfo));
			updateProvider?.RegisterUpdatesProvider(new PortosRadioUpdatesProvider(extensionInfo, runtimeInfo));
		}
		else {
			Api.Get<ILoggerService>(LoggerService.ServiceName).Warning(PortosService.ServiceName, "Portos device update provider not registered - controller does not support Portos extension.");
		}
	}
	
	GetDeviceServiceByServiceType<T extends IDeviceConfigService>(classType: string): T {
		switch (classType) {
			case "Portos433ConfigService" : 
				return this._services.Portos433ConfigService as unknown as T;
			default:
				return null as unknown as T;
		}
	}

	GetDeviceService<T extends IDeviceConfigService>(device: IDevice, classType: string): T {
		let services = this.GetAllDeviceServices(device);

		return services !== null ? services.firstOrDefault(service => service.GetServiceName() === classType) as unknown as T : null as unknown as T;
	}

	GetAllDeviceServices(device: IDevice): IDeviceConfigService[] {
		if (device.ModelGuid !== null) {
			const services = this._servicesToDeviceMap.get(device.ModelGuid);
			return services !== undefined ? services : [];
		}
		else return []
	}

	IsDeviceServiceSupported<T extends IDeviceConfigService>(device: IDevice, classType: string): boolean {
		return this.GetDeviceService(device, classType) !== null ? true : false;
	}
}